import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Analytics() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [habits, setHabits] = useState([]);

  useEffect(() => {
    api.get("/analytics/summary").then((r) => setSummary(r.data));
    api.get("/habits").then((r) => setHabits(r.data));
  }, []);

  const exportPDF = () => {
    if (typeof window === "undefined") return;
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("Enable popups to export");
      return;
    }
    const habitsCounts = summary?.habit_counts || {};
    const rowsHtml = habits
      .map(
        (h) =>
          `<tr><td>${h.name}</td><td style="text-align:right">${habitsCounts[h.key] || 0}</td></tr>`
      )
      .join("");
    const totalXP = (summary?.days || []).reduce((a, d) => a + d.xp, 0);
    const totalQuests = (summary?.days || []).reduce((a, d) => a + d.completed_count, 0);
    w.document.write(`<!doctype html><html><head><title>Solo Ascend — Progress Report</title>
      <style>
        body{background:#05050A;color:#F8FAFC;font-family:sans-serif;padding:40px;}
        h1{color:#00E5FF;letter-spacing:2px}
        h2{color:#8B5CF6;margin-top:30px}
        table{width:100%;border-collapse:collapse}
        td,th{padding:8px;border-bottom:1px solid #333;text-align:left}
        .stat{display:inline-block;padding:15px 25px;border:1px solid #00E5FF;margin-right:10px}
        .stat b{color:#00E5FF;font-size:22px;display:block}
      </style></head><body>
      <h1>SOLO ASCEND — 30-DAY REPORT</h1>
      <p>Hunter: <b>${user?.name || ""}</b> · LVL ${user?.level ?? 1} · ${user?.avatar_stage?.replace("_", " ").toUpperCase()}</p>
      <div style="margin-top:20px">
        <span class="stat"><b>${totalQuests}</b> Quests Cleared</span>
        <span class="stat"><b>${totalXP}</b> XP Earned</span>
        <span class="stat"><b>${user?.current_streak ?? 0}</b> Day Streak</span>
        <span class="stat"><b>${user?.achievements?.length ?? 0}</b> Achievements</span>
      </div>
      <h2>Habit Completions (30 days)</h2>
      <table><thead><tr><th>Habit</th><th style="text-align:right">Count</th></tr></thead>
      <tbody>${rowsHtml}</tbody></table>
      <p style="margin-top:40px;color:#94A3B8;font-size:12px">Print → Save as PDF · Solo Ascend System</p>
      <script>window.print()</script>
      </body></html>`);
    w.document.close();
  };

  if (!summary) return <div className="label-tech animate-pulse">Loading analytics...</div>;

  const habitData = habits.map((h) => ({
    name: h.name.replace(/[()]/g, "").slice(0, 14),
    count: summary.habit_counts[h.key] || 0,
  }));
  const days = summary.days.map((d) => ({ day: d.day.slice(5), xp: d.xp, quests: d.completed_count }));

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-tech">Analytics</div>
          <h1 className="h-display text-3xl sm:text-4xl mt-1">
            30-Day <span className="text-cyan-400">Report</span>
          </h1>
        </div>
        <button data-testid="export-pdf" onClick={exportPDF} className="btn-neon">
          <Download size={16} className="inline mr-1" /> Export PDF
        </button>
      </div>

      <div className="panel panel-corners p-6">
        <div className="label-tech mb-3">XP Earned per Day</div>
        <div className="h-64">
          <ResponsiveContainer>
            <LineChart data={days}>
              <CartesianGrid stroke="rgba(139,92,246,0.15)" strokeDasharray="3 3" />
              <XAxis dataKey="day" stroke="#64748b" fontSize={10} interval={4} />
              <YAxis stroke="#64748b" fontSize={11} />
              <Tooltip contentStyle={{ background: "#0F0F16", border: "1px solid #00E5FF" }} />
              <Line type="monotone" dataKey="xp" stroke="#00E5FF" strokeWidth={2} dot={{ r: 2, fill: "#00E5FF" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="panel panel-corners p-6">
        <div className="label-tech mb-3">Habit Frequency (30d)</div>
        <div className="h-72">
          <ResponsiveContainer>
            <BarChart data={habitData} layout="vertical" margin={{ left: 40 }}>
              <CartesianGrid stroke="rgba(139,92,246,0.15)" strokeDasharray="3 3" />
              <XAxis type="number" stroke="#64748b" fontSize={11} />
              <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={10} width={100} />
              <Tooltip contentStyle={{ background: "#0F0F16", border: "1px solid #00E5FF" }} />
              <Bar dataKey="count" fill="#8B5CF6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
